import { transitionController, PageEvent } from '../controllers/transition.controller';
import { headerController } from '../controllers/header.controller';
import CONFIG from '../config/config.json';
import { ColorCoverTransition } from '../controllers/transitions/ColorCover.transition';
import { scrollController } from '../controllers/scroll.controller';
import { BlockEvent, blockLifecycleController } from '../controllers/block-lifecycle.controller';
import rosController from '../controllers/ros.controller';

export function setupTransitions(): void {
	const LOG_PREFIX = '[TransitionsSetup]';
	const DEBUG_VERBOSE = false;

	const onDomContentLoaded = () => {
		// Manually dispatch PageEvents that will be handled by a page-transition system
		// beyond the initial page load. This ensures that the sequencing of components,
		// menus etc. that use the page-lifecycle system is consistent.
		// This should be done even if Barba is not initialised.
		// console.log(LOG_PREFIX, 'onDomContentLoaded()');
		document.dispatchEvent(new CustomEvent(PageEvent.enter, { detail: { mimic: true } }));
		if (!transitionController) {
			// Barba's afterEnter hook is activated on hard page load, but if Barba isn't initialised, we
			// must dispatch it, in addition to PageEvent.enter, to ensure the page lifecycle is fully mirrored.
			document.dispatchEvent(new CustomEvent(PageEvent.afterEnter, { detail: { mimic: true } }));
		}
	};

	// Once the DOM is ready, we can trigger the initial page enter event
	if (document.readyState === 'loading') {
		onDomContentLoaded();
	} else {
		window.addEventListener('DOMContentLoaded', () => onDomContentLoaded());
	}

	blockLifecycleController.subscribe('transitions.setup', async (eventName: string, _data?: any) => {
		if (eventName === BlockEvent.BlocksFinalConfigComplete) {
			//console.log(LOG_PREFIX, 'AllBlocksLoaded');
			document.body.classList.remove('hhds-cloak-hide');
			// Once modules are fully loaded, manually navigate to the URL hash
			const hash = window.location.hash;
			const target = scrollController.getElementFromAnchorHref(hash);
			// If performing a back navigation, the URL hash should not take precedence over restoration of previous scroll position.
			if (!transitionController?.lastNavigationWasBackward) {
				if (target) {
					requestAnimationFrame(() => {
						scrollController.scrollToElementForHash(target, false, true);
						requestAnimationFrame(() => {
							rosController.configureBlockRevealOnScroll();
						});
					});
				}
			}
		}
	});

	// Transition controller will be null if PAGE_TRANSITIONS is false in config,
	// so we check for this before attempting to initialise, but we still want to
	// dispatch the initial page events on load even if there isn't a transition system
	if (!transitionController) {
		DEBUG_VERBOSE && console.warn('Page transitions are disabled in config. No transitions will be used.');
		return;
	}

	// Initialise Barba if there is no WP Admin Bar present
	if (document.querySelector('#wpadminbar')) {
		DEBUG_VERBOSE && console.warn('WP Admin Bar detected. Page transitions disabled to avoid conflicts.');
		return;
	}

	// Initialise the transition controller, specifying the default transition
	transitionController.init({
		defaultTransition: ColorCoverTransition,
		scrollToTopOnTransition: true,
		routes: CONFIG.BARBA_ROUTES,
	});

	// Configure the page ready callback to perform any actions required when a new page has loaded
	// but before the transition "enter" animation has completed. This is where we would typically
	// trigger any animations that should be synced with the transition, e.g. AOS animations on the
	// new page.
	transitionController.pageReadyCallback = async (_data: any): Promise<void> => {
		// The transition controller already emits a nextReady event (i.e. incoming page ready) after
		// this pageReadyCallback has completed, but we dispatch a separate event here to allow other
		// parts of our frontend to respond after an animation frame, and as part of the page ready sequence.
		// This allows us to do things like update page scroll accurately, before the transition controller
		// recognises the page as ready and reveals the new page as part of its transition effect.
		await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
		blockLifecycleController.subscribe('transitions.setup-pageReadyCallback', async (eventName: string, _data?: any) => {
			if (eventName === BlockEvent.BlocksFinalConfigComplete) {
				document.dispatchEvent(new CustomEvent('page-ready-post-layout'));
				DEBUG_VERBOSE && console.log(LOG_PREFIX, 'Page load-handling complete!');
				blockLifecycleController.unsubscribe('transitions.setup-pageReadyCallback');
				return Promise.resolve();
			}
		});
	};

	document.addEventListener(PageEvent.leave, (_event: any) => {
		// Remove focus from the active element when leaving a page.
		// This is required to unfocus the menu, and we may want to have a more
		// targeted approach than this, e.g. to only remove focus if it's within a
		// problematic area of the page being left.
		const activeElement: HTMLElement = document.activeElement as HTMLElement;
		activeElement?.blur();
	});
	document.addEventListener(PageEvent.beforeLeave, (_event: any) => {
		// Close the menu
		headerController.closeMenu();
	});
}
